import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { SectionLabel } from '../BrandCampaignDetailPage';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_LUXE_ERROR,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from '../brandDashboardTokens';
import type { IDeliverableRound } from '../../../redux/buzzbridge/types';
import type { IRoundPayout, IWorkspaceContentSubmission } from '../../../redux/buzzbridge/campaignWorkspace.types';
import { DELIVERABLE_TYPE_LABELS } from '../../../utils/deliverableLabels';

export type DeliverableRowStatus = 'pending' | 'submitted' | 'approved' | 'revision';

export interface IDeliverableRow {
    label: string;
    status: DeliverableRowStatus;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; Icon: React.ElementType }> = {
    pending: { label: 'Pending', color: 'rgba(255,255,255,0.35)', Icon: HourglassEmptyOutlinedIcon },
    submitted: { label: 'Submitted', color: BRAND_LUXE_PRIMARY, Icon: CheckCircleOutlineIcon },
    approved: { label: 'Approved', color: BRAND_LUXE_SECONDARY, Icon: TaskAltIcon },
    revision: { label: 'Revision', color: BRAND_LUXE_ERROR, Icon: LoopOutlinedIcon },
};

function submissionStatusToRow(status: string | undefined): DeliverableRowStatus {
    switch (status) {
        case 'approved':
        case 'shared':
            return 'approved';
        case 'submitted':
            return 'submitted';
        case 'needs_revision':
            return 'revision';
        default:
            return 'pending';
    }
}

function isRoundFullyApproved(
    round: IDeliverableRound,
    submissions: IWorkspaceContentSubmission[],
): boolean {
    const roundSubs = submissions.filter(s =>
        s.deliverable_round_id
            ? s.deliverable_round_id === round._id
            : (s as any).round_number === round.round_number,
    );
    return roundSubs.length > 0 && roundSubs.every(s => s.status === 'approved' || s.status === 'shared');
}

export function ParticipantDeliverables({
    items,
    deliverableRounds,
    roundPayouts,
    submissions,
    activeRoundIdx,
}: {
    items: IDeliverableRow[];
    deliverableRounds?: IDeliverableRound[];
    roundPayouts?: IRoundPayout[];
    submissions?: IWorkspaceContentSubmission[];
    activeRoundIdx?: number;
}) {
    const isRecurrent = Boolean(deliverableRounds?.length && deliverableRounds.length > 1);

    if (isRecurrent && deliverableRounds && submissions !== undefined) {
        const rounds = deliverableRounds;
        const totalRounds = rounds.length;
        const currentIdx = activeRoundIdx ?? 0;
        const activeRound = rounds[currentIdx];

        // Build deliverable rows for the active round
        const roundDeliverables = activeRound?.deliverables?.length
            ? activeRound.deliverables
            : null;
        const roundSubs = submissions.filter(s =>
            activeRound
                ? s.deliverable_round_id
                    ? s.deliverable_round_id === activeRound._id
                    : (s as any).round_number === activeRound.round_number
                : false,
        );

        const rows: IDeliverableRow[] = roundDeliverables
            ? roundDeliverables.map(d => {
                  const sub = roundSubs.find(s => s.deliverable_type === d.type);
                  return {
                      label: DELIVERABLE_TYPE_LABELS[d.type] ?? d.type,
                      status: submissionStatusToRow(sub?.status),
                  };
              })
            : roundSubs.length > 0
            ? roundSubs.map(s => ({
                  label:
                      (s.deliverable_type && (DELIVERABLE_TYPE_LABELS[s.deliverable_type] ?? s.deliverable_type)) ||
                      'Deliverable',
                  status: submissionStatusToRow(s.status),
              }))
            : [{ label: 'Deliverables', status: 'pending' as DeliverableRowStatus }];

        const completedRows = rows.filter(r => r.status === 'approved').length;

        // Count fully approved rounds
        const paidRounds = rounds.filter(r =>
            (roundPayouts ?? []).some(
                p => p.round_id === r._id || p.round_number === r.round_number,
            ),
        ).length;

        return (
            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, borderRadius: 2, p: 3, position: 'sticky', top: 24 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <SectionLabel>Deliverables</SectionLabel>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: BRAND_LUXE_SECONDARY }}>
                        {paidRounds}/{totalRounds} paid
                    </Typography>
                </Box>

                {/* Multi-step delivery progress bar */}
                <Box sx={{ mb: 0.75 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {rounds.map((r, i) => {
                            const paid = (roundPayouts ?? []).some(
                                p => p.round_id === r._id || p.round_number === r.round_number,
                            );
                            const fullyApproved = !paid && isRoundFullyApproved(r, submissions);
                            const isActive = i === currentIdx;
                            return (
                                <Box
                                    key={r._id ?? i}
                                    sx={{
                                        flex: 1,
                                        height: 5,
                                        borderRadius: 999,
                                        bgcolor: paid
                                            ? BRAND_LUXE_SECONDARY
                                            : fullyApproved
                                            ? `${BRAND_LUXE_SECONDARY}70`
                                            : isActive
                                            ? BRAND_LUXE_PRIMARY
                                            : BRAND_SURFACE_CONTAINER_HIGHEST,
                                        boxShadow: paid
                                            ? '0 0 5px rgba(0,253,193,0.5)'
                                            : isActive
                                            ? '0 0 5px rgba(255,201,101,0.5)'
                                            : undefined,
                                        transition: 'background-color 0.3s',
                                    }}
                                />
                            );
                        })}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75, mb: 2 }}>
                        <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            Delivery {(activeRound?.round_number ?? currentIdx + 1)}{activeRound?.label ? ` · ${activeRound.label}` : ''}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            {currentIdx + 1} of {totalRounds}
                        </Typography>
                    </Box>
                </Box>

                {/* Deliverable rows for active round */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ fontSize: 10, fontFamily: FONT_ENUM.MAIN_BOLD, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)' }}>
                        This delivery
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 11, color: BRAND_LUXE_SECONDARY }}>
                        {completedRows}/{rows.length} done
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {rows.map((d, i) => {
                        const cfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.pending;
                        const Icon = cfg.Icon;
                        return (
                            <Box
                                key={i}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    py: 1.5,
                                    px: 2,
                                    borderRadius: 1.5,
                                    bgcolor: d.status === 'approved' ? 'rgba(0,253,193,0.04)' : 'transparent',
                                    border: `1px solid ${d.status === 'revision' ? `${BRAND_LUXE_ERROR}30` : 'transparent'}`,
                                }}>
                                <Icon sx={{ fontSize: 18, color: cfg.color, flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 13, color: '#fff' }}
                                        noWrap>
                                        {d.label}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 10,
                                        color: cfg.color,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em',
                                        flexShrink: 0,
                                    }}>
                                    {cfg.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    }

    // Non-recurrent: original behaviour
    const completed = items.filter(d => d.status === 'approved').length;
    const total = items.length;

    return (
        <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, borderRadius: 2, p: 3, position: 'sticky', top: 24 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <SectionLabel>Deliverables</SectionLabel>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: BRAND_LUXE_SECONDARY }}>
                    {completed}/{total} Complete
                </Typography>
            </Box>

            {/* Progress bar */}
            <Box
                sx={{
                    width: '100%',
                    height: 4,
                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                    borderRadius: 999,
                    mb: 3,
                    overflow: 'hidden',
                }}>
                <Box
                    sx={{
                        height: '100%',
                        borderRadius: 999,
                        bgcolor: BRAND_LUXE_SECONDARY,
                        width: `${total > 0 ? (completed / total) * 100 : 0}%`,
                        boxShadow: '0 0 8px rgba(0,253,193,0.4)',
                        transition: 'width 0.4s ease',
                    }}
                />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {items.map((d, i) => {
                    const cfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.pending;
                    const Icon = cfg.Icon;
                    return (
                        <Box
                            key={i}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                py: 1.5,
                                px: 2,
                                borderRadius: 1.5,
                                bgcolor: d.status === 'approved' ? 'rgba(0,253,193,0.04)' : 'transparent',
                                border: `1px solid ${
                                    d.status === 'revision' ? `${BRAND_LUXE_ERROR}30` : 'transparent'
                                }`,
                            }}>
                            <Icon sx={{ fontSize: 18, color: cfg.color, flexShrink: 0 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 13, color: '#fff' }}
                                    noWrap>
                                    {d.label}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 10,
                                    color: cfg.color,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                    flexShrink: 0,
                                }}>
                                {cfg.label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
